import type { SelectedFilter } from "~/components/build/Show/components/Filter";
import PanelTitle from "~/components/build/Show/components/PanelTitle";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { getAnnotationCounts } from "~/components/build/Show/lib/annotationCounts";
import { SCOPE_CATEGORY_KEY } from "~/components/build/Show/lib/annotationFilters";
import type { AnnotationScope } from "~/components/build/Show/lib/types";

interface AnnotationsCountProps {
  /**
   * The currently selected filters from the AnnotationsPanel. Taking the
   * generic filter list (rather than scopes only) means future filter
   * categories surface in the count automatically.
   */
  selectedFilters: SelectedFilter[];
}

/**
 * Renders the annotation count summary for the AnnotationsPanel header.
 *
 * Shows just "Annotations" when the build has no annotations, "Annotations {totalCount}"
 * by default, and "Annotations {filteredCount} of {totalCount}" when active filters
 * actually narrow the visible results.
 */
export default function AnnotationsCount({ selectedFilters }: AnnotationsCountProps) {
  const { build } = useBuild();
  const annotationCounts = getAnnotationCounts(build);
  const hasAnnotations = annotationCounts.totalCount > 0;

  const selectedScopes = selectedFilters
    .filter((filter) => filter.categoryKey === SCOPE_CATEGORY_KEY)
    .map((filter) => filter.value as AnnotationScope);

  // Selecting zero or all scopes is equivalent to no scope filter — the list
  // returns every annotation in both cases (see scopesToScope in AnnotationsList).
  const filteredCount =
    selectedScopes.length === 0
      ? annotationCounts.totalCount
      : selectedScopes.reduce((sum, scope) => sum + annotationCounts[scope], 0);

  const isFiltered = hasAnnotations && filteredCount !== annotationCounts.totalCount;

  return (
    <PanelTitle
      testId="annotations-count"
      label="Annotations"
      count={hasAnnotations ? filteredCount : undefined}
      totalCount={isFiltered ? annotationCounts.totalCount : undefined}
    />
  );
}
